import { toMs } from './timeFormat';
import type { LiveSessionEvent } from './types';

export type LaneState = 'disconnected' | 'following' | 'free' | 'off_page';

export interface Interval {
  end: number;
  start: number;
  state: LaneState;
}

export interface TimeRange {
  end: number;
  start: number;
}

interface StudentState {
  flags: { freeSuspect: boolean; stuckSuspect: boolean };
  intervals: Interval[];
  studentId: number;
}

export interface DerivedStates {
  end: number;
  start: number;
  students: Record<number, StudentState>;
  teacherDrops: TimeRange[];
}

interface DeriveOptions {
  endedAt: string | null;
  invitedIds: Set<number>;
  now?: number;
  startedAt: string;
}

const STUCK_DISCONNECT_MS = 10_000;
const FOLLOW_GRACE_MS = 10_000;
const FREE_MIN_MS = 10_000;

interface Tracker {
  locked: boolean;
  onPage: boolean;
  // null until the first socket signal: a hydrated-locked student is assumed connected until a
  // drop is actually reported.
  ws: boolean | null;
}

const stateOf = ({ locked, onPage, ws }: Tracker): LaneState => {
  if (!onPage) return 'off_page';
  if (!locked) return 'free';
  return ws === false ? 'disconnected' : 'following';
};

// Server rows fill in when a student's browser never reported (older sessions, blocked beacon).
const applyEvent = (tracker: Tracker, event: LiveSessionEvent): Tracker => {
  const next = { ...tracker };
  switch (event.event_type) {
    case 'page_entered':
      next.onPage = true;
      break;
    case 'present_mode_hydrated':
      next.onPage = true;
      if (event.metadata.result === 'locked') next.locked = true;
      if (event.metadata.result === 'free') next.locked = false;
      break;
    case 'page_left':
      next.onPage = false;
      next.ws = false;
      break;
    case 'lock_engaged':
      next.onPage = true;
      next.locked = true;
      break;
    case 'lock_released':
      next.locked = false;
      break;
    case 'ws_connected':
    case 'channel_subscribed':
    case 'mark_present':
      next.onPage = true;
      next.ws = true;
      break;
    case 'ws_disconnected':
    case 'ws_rejected':
    case 'channel_unsubscribed':
    case 'mark_absent':
      next.ws = false;
      break;
    case 'student_removed_received':
      next.locked = false;
      break;
    default:
      break;
  }
  return next;
};

const closeInterval = (intervals: Interval[], at: number) => {
  const open = intervals[intervals.length - 1];
  if (open && open.end === Infinity) open.end = at;
};

const pushState = (intervals: Interval[], state: LaneState, at: number) => {
  const open = intervals[intervals.length - 1];
  if (open && open.state === state) return;
  closeInterval(intervals, at);
  intervals.push({ end: Infinity, start: at, state });
};

const buildIntervals = (events: LiveSessionEvent[], start: number, end: number): Interval[] => {
  const intervals: Interval[] = [];
  let tracker: Tracker = { locked: false, onPage: false, ws: null };
  pushState(intervals, stateOf(tracker), start);

  events.forEach((event) => {
    tracker = applyEvent(tracker, event);
    pushState(intervals, stateOf(tracker), Math.max(start, toMs(event.occurred_at)));
  });

  closeInterval(intervals, end);
  return intervals.filter(interval => interval.end > interval.start);
};

const stateAt = (intervals: Interval[], at: number): LaneState | null => (
  intervals.find(interval => interval.start <= at && at < interval.end)?.state ?? null
);

// A locked, connected student who does not show a teacher-sent slide within the grace window is
// the "stuck but nothing looks wrong" case that motivated the event log.
const missedTeacherNav = (
  intervals: Interval[], studentEvents: LiveSessionEvent[], teacherNavs: number[], end: number,
): boolean => {
  const applied = studentEvents.filter(e => e.event_type === 'nav_applied').map(e => toMs(e.occurred_at));
  return teacherNavs.some((navAt) => {
    if (navAt + FOLLOW_GRACE_MS > end) return false;
    if (stateAt(intervals, navAt) !== 'following') return false;
    return !applied.some(at => at >= navAt && at <= navAt + FOLLOW_GRACE_MS);
  });
};

const teacherDropRanges = (events: LiveSessionEvent[], end: number): TimeRange[] => {
  const ranges: TimeRange[] = [];
  let openStart: number | null = null;
  events.forEach((event) => {
    if (event.event_type === 'teacher_disconnected' && openStart === null) openStart = toMs(event.occurred_at);
    if (event.event_type === 'teacher_reconnected' && openStart !== null) {
      ranges.push({ end: toMs(event.occurred_at), start: openStart });
      openStart = null;
    }
  });
  if (openStart !== null) ranges.push({ end, start: openStart });
  return ranges;
};

export const deriveParticipantStates = (
  events: LiveSessionEvent[],
  { endedAt, invitedIds, now = Date.now(), startedAt }: DeriveOptions,
): DerivedStates => {
  const start = toMs(startedAt);
  const end = endedAt ? toMs(endedAt) : now;
  const teacherNavs = events
    .filter(e => e.event_type === 'navigate_to_slide' && !e.metadata.rejected_reason)
    .map(e => toMs(e.occurred_at));

  const byStudent = new Map<number, LiveSessionEvent[]>();
  events.forEach((event) => {
    if (event.actor?.type !== 'Student') return;
    const list = byStudent.get(event.actor.id) ?? [];
    list.push(event);
    byStudent.set(event.actor.id, list);
  });

  const students: Record<number, StudentState> = {};
  byStudent.forEach((studentEvents, studentId) => {
    const intervals = buildIntervals(studentEvents, start, end);
    const hydratedFree = studentEvents.some(
      e => e.event_type === 'present_mode_hydrated' && e.metadata.result === 'free',
    );
    const longDisconnect = intervals.some(
      i => i.state === 'disconnected' && i.end - i.start >= STUCK_DISCONNECT_MS,
    );
    const longFree = intervals.some(i => i.state === 'free' && i.end - i.start >= FREE_MIN_MS);

    students[studentId] = {
      flags: {
        freeSuspect: longFree && (invitedIds.has(studentId) || hydratedFree),
        stuckSuspect: longDisconnect
          || studentEvents.some(e => e.event_type === 'nav_unresolved')
          || missedTeacherNav(intervals, studentEvents, teacherNavs, end),
      },
      intervals,
      studentId,
    };
  });

  return { end, start, students, teacherDrops: teacherDropRanges(events, end) };
};
